import Modal from '@/components/common/modal';
import {
  formatInclusiveLastServiceDayFromExclusiveEndUtc,
  formatSubscriptionBillingInstantUtcMedium,
} from '@/helpers/organization-subscription-pricing';
import { useTranslations } from '@/i18n/use-translations';
import { parseISO } from 'date-fns';
import { Box, Button, Divider, Stack, Typography, Chip, Alert } from '@mui/material';
import type { OrganizationPlanChangePreview } from '@/types/dtos/transactions/organization-plan-change-preview.dto';

function formatMoney(n: number): string {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR' }).format(n);
}

function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(new Date(iso));
  } catch {
    return iso;
  }
}

/** User-facing “remaining period”: subscription row is half-open `[start, exclusiveEnd)`; show last inclusive service day before renewal (UTC calendar). */
function formatProrataRemainingPeriodLabel(
  preview: OrganizationPlanChangePreview,
  pr: NonNullable<OrganizationPlanChangePreview['prorataEstimate']>,
): string {
  if (preview.billingPolicy !== 'prorata_upgrade') {
    return `${formatDate(pr.windowStart)} – ${formatDate(pr.windowEnd)}`;
  }
  try {
    const pe = parseISO(preview.periodEndAfterPayment);
    if (Number.isNaN(pe.getTime())) {
      return `${formatDate(pr.windowStart)} – ${formatDate(pr.windowEnd)}`;
    }
    const ps = parseISO(preview.periodStartAfterPayment);
    const startLabel = Number.isNaN(ps.getTime())
      ? formatDate(preview.periodStartAfterPayment)
      : formatSubscriptionBillingInstantUtcMedium(ps);
    const endLabel = formatInclusiveLastServiceDayFromExclusiveEndUtc(pe);
    return `${startLabel} – ${endLabel}`;
  } catch {
    return `${formatDate(pr.windowStart)} – ${formatDate(pr.windowEnd)}`;
  }
}

export default function SubscriptionPlanChangeConfirmModal({
  open,
  onClose,
  preview,
  targetTierLabel,
  billingLabel,
  onConfirm,
  confirmLoading,
}: {
  open: boolean;
  onClose: () => void;
  preview: OrganizationPlanChangePreview | null;
  targetTierLabel: string;
  billingLabel: string;
  onConfirm: () => void;
  confirmLoading: boolean;
}) {
  const t = useTranslations();

  if (!preview) {
    return null;
  }

  const pr = preview.prorataEstimate;
  const isProrataUpgrade = preview.billingPolicy === 'prorata_upgrade';

  return (
    <Modal
      open={open}
      handleClose={onClose}
      title={t('subscription.organization.planPage.planChangeConfirm.title', { plan: targetTierLabel })}
      description={t('subscription.organization.planPage.planChangeConfirm.subtitle')}
      size="sm"
      contentSx={{ maxHeight: 'min(85vh, 640px)', overflowY: 'auto' }}
    >
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Typography variant="subtitle1" fontWeight={600}>
            {targetTierLabel}
          </Typography>
          <Chip size="small" label={billingLabel} variant="outlined" />
        </Stack>

        <Typography variant="body2" color="text.secondary">
          {isProrataUpgrade
            ? t('subscription.organization.planPage.planChangeConfirm.policyProrataUpgrade')
            : t('subscription.organization.planPage.planChangeConfirm.policyInitialCheckout')}
        </Typography>

        <Divider />

        <Stack spacing={1}>
          <Stack direction="row" justifyContent="space-between" alignItems="baseline">
            <Typography variant="body2" color="text.secondary">
              {t('subscription.organization.planPage.planChangeConfirm.dueToday')}
            </Typography>
            <Typography variant="h5" component="span" fontWeight={700}>
              {formatMoney(preview.chargeNow)}
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              {t('subscription.organization.planPage.planChangeConfirm.recurringLabel')}
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {formatMoney(preview.recurringFullPeriodPrice)}
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              {isProrataUpgrade
                ? t('subscription.organization.planPage.planChangeConfirm.currentPeriodEnds')
                : t('subscription.organization.planPage.planChangeConfirm.firstPeriodEnds')}
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {formatDate(preview.periodEndAfterPayment)}
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              {t('subscription.organization.planPage.planChangeConfirm.nextChargeDate')}
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {formatDate(preview.nextRecurringChargeDate)}
            </Typography>
          </Stack>
        </Stack>

        {pr ? (
          <>
            <Divider />
            <Typography variant="subtitle2" color="text.secondary">
              {t('subscription.organization.planPage.planChangeConfirm.prorataSectionTitle')}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {t('subscription.organization.planPage.planChangeConfirm.prorataSectionHint')}
            </Typography>
            <Box
              component="dl"
              sx={{
                'm': 0,
                'display': 'grid',
                'gap': 1,
                '& dt': { color: 'text.secondary', fontSize: '0.8125rem' },
                '& dd': { m: 0, fontWeight: 500 },
              }}
            >
              <div>
                <dt>{t('subscription.organization.planPage.planChangeConfirm.prorataWindow')}</dt>
                <dd>{formatProrataRemainingPeriodLabel(preview, pr)}</dd>
              </div>
              {pr.prorataChargeBasisFullPeriodPrice != null &&
              Math.abs(pr.prorataChargeBasisFullPeriodPrice - pr.newPlanFullPeriodPrice) > 1e-6 ? (
                <div>
                  <dt>{t('subscription.organization.planPage.planChangeConfirm.prorataChargeBasisFullPeriod')}</dt>
                  <dd>{formatMoney(pr.prorataChargeBasisFullPeriodPrice)}</dd>
                </div>
              ) : null}
              {pr.estimatedCreditForUnusedTime > 1e-6 ? (
                <div>
                  <dt>{t('subscription.organization.planPage.planChangeConfirm.estimatedCredit')}</dt>
                  <dd>{formatMoney(pr.estimatedCreditForUnusedTime)}</dd>
                </div>
              ) : null}
              {pr.estimatedChargeForNewPlanPortion > 1e-6 ? (
                <div>
                  <dt>{t('subscription.organization.planPage.planChangeConfirm.estimatedNewPortion')}</dt>
                  <dd>{formatMoney(pr.estimatedChargeForNewPlanPortion)}</dd>
                </div>
              ) : null}
              <div>
                <dt>{t('subscription.organization.planPage.planChangeConfirm.estimatedNetRemainder')}</dt>
                <dd>{formatMoney(pr.estimatedNetDueForRemainder)}</dd>
              </div>
            </Box>
            {isProrataUpgrade ? (
              <Stack spacing={1}>
                {pr?.prorataChargeBasisFullPeriodPrice != null &&
                Math.abs(pr.prorataChargeBasisFullPeriodPrice - pr.newPlanFullPeriodPrice) > 1e-6 ? (
                  <Alert severity="info" variant="outlined">
                    {t('subscription.organization.planPage.planChangeConfirm.prorataDeferredQuotaNote')}
                  </Alert>
                ) : null}
                <Alert severity="info" variant="outlined">
                  {t('subscription.organization.planPage.planChangeConfirm.prorataMatchesCheckout')}
                </Alert>
              </Stack>
            ) : null}
          </>
        ) : null}

        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ pt: 1 }}>
          <Button type="button" onClick={onClose} disabled={confirmLoading}>
            {t('common.cancel')}
          </Button>
          <Button type="button" variant="contained" onClick={onConfirm} loading={confirmLoading}>
            {preview.chargeNow === 0
              ? t('subscription.organization.planPage.planChangeConfirm.saveToCartApply')
              : t('subscription.organization.planPage.planChangeConfirm.saveToCart')}
          </Button>
        </Stack>
      </Stack>
    </Modal>
  );
}
